/**
 * Tier-2 Configure draft state (spec 2026-07-04 §7) — composerStore's
 * draft/dirty pattern applied to the overlay grammar. The store builds a
 * SPARSE overlay: guided setters compare against the base definition and
 * prune entries that become no-ops, so what we PUT is exactly what
 * differs. Dirtiness compares overlay vs the last SAVED draft.
 */
import { create } from 'zustand'
import type { OrderWorkflowDefinitionJson } from '@/types/workflowDefinition'
import type { WorkflowOverlayJson, WorkflowConfigValidateResponse } from '@/types/workflowConfig'

function deepEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

/** Delete `path`'s leaf and collapse now-empty parent objects. */
function pruneEmpty(obj: Record<string, unknown>, path: string[]): void {
  if (path.length === 0) return
  const [head, ...rest] = path
  const child = obj[head]
  if (rest.length > 0 && child && typeof child === 'object') {
    pruneEmpty(child as Record<string, unknown>, rest)
  }
  const value = obj[head]
  if (
    value !== undefined &&
    typeof value === 'object' &&
    value !== null &&
    Object.keys(value).length === 0
  ) {
    delete obj[head]
  }
}

/** Delete `path`'s leaf (if present) and collapse now-empty parents. */
function deletePath(obj: Record<string, unknown>, path: string[]): void {
  let cursor: Record<string, unknown> = obj
  for (const seg of path.slice(0, -1)) {
    const child = cursor[seg]
    if (!child || typeof child !== 'object') return
    cursor = child as Record<string, unknown>
  }
  delete cursor[path[path.length - 1]]
  pruneEmpty(obj, path.slice(0, -1))
}

type KeyedSection = 'slots' | 'needs' | 'derived' | 'side_effects'

/** Schema defaults for trigger fields (backend EscalationTrigger) — lets
 * re-enabling / resetting to a default prune even when the base trigger
 * omits the field. */
const TRIGGER_FIELD_DEFAULTS: Record<string, unknown> = {
  enabled: true,
  mode: 'hard',
  after: 1,
  // action/target editing is API-only in v1; the default keeps setTriggerField
  // pruning correct if a future editor writes it back to 'escalate'.
  action: 'escalate',
}

function findBaseTrigger(
  base: OrderWorkflowDefinitionJson | null,
  id: string,
): Record<string, unknown> | undefined {
  return (base?.escalation?.triggers ?? []).find((t) => t.id === id) as
    | Record<string, unknown>
    | undefined
}

/** Schema default for signal fields (backend SignalSpec) — lets re-enabling
 * prune even when the base signal omits `enabled`. */
const SIGNAL_FIELD_DEFAULTS: Record<string, unknown> = {
  enabled: true,
}

function findBaseSignal(
  base: OrderWorkflowDefinitionJson | null,
  id: string,
): Record<string, unknown> | undefined {
  return (base?.signals ?? []).find((s) => s.id === id) as
    | Record<string, unknown>
    | undefined
}

function setOrPrune(
  overlay: WorkflowOverlayJson,
  path: string[],
  value: unknown,
  baseValue: unknown,
): WorkflowOverlayJson {
  const next = clone(overlay) as Record<string, unknown>
  let cursor: Record<string, unknown> = next
  for (const segment of path.slice(0, -1)) {
    cursor[segment] = { ...(cursor[segment] as Record<string, unknown> | undefined) }
    cursor = cursor[segment] as Record<string, unknown>
  }
  const leaf = path[path.length - 1]
  if (deepEqual(value, baseValue)) {
    delete cursor[leaf]
    pruneEmpty(next, path.slice(0, -1))
  } else {
    cursor[leaf] = value
  }
  return next as WorkflowOverlayJson
}

function sameMembership(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((k) => b.includes(k))
}

/** A key edits the `needs` overlay section when the base declares it as a
 * need, or the overlay carries it as an overlay-added need (SA-83 orphan
 * shape) — EXCEPT when the base declares it as a slot (a colliding needs
 * entry must never capture a base slot's edits). */
function isNeedKey(
  base: OrderWorkflowDefinitionJson | null,
  overlay: WorkflowOverlayJson,
  key: string,
): boolean {
  if (base?.slots && key in base.slots) return false
  if (base?.needs && key in base.needs) return true
  return !!overlay.needs && key in overlay.needs
}

/** True if `needs.<key>` is still present on the (already-pruned) overlay. */
function needEntryPresent(overlay: Record<string, unknown>, key: string): boolean {
  const needs = overlay.needs as Record<string, unknown> | undefined
  return !!needs && key in needs
}

interface WorkflowConfigState {
  workflowId: string | null
  base: OrderWorkflowDefinitionJson | null
  overlay: WorkflowOverlayJson
  savedDraft: WorkflowOverlayJson | null
  /** SA-416: what this session's writes are based on — the stored draft's
   * stamp (null = no stamped draft) and the config base's version number. */
  expectedUpdatedAt: string | null
  baseVersionNumber: number | null
  /** SA-1410: the live version number the session began on (null = none). */
  versionNumber: number | null
  editing: boolean
  validation: WorkflowConfigValidateResponse | null
  advanced: boolean

  beginEditing(
    workflowId: string,
    base: OrderWorkflowDefinitionJson,
    savedDraft: WorkflowOverlayJson | null,
    expectedUpdatedAt?: string | null,
    baseVersionNumber?: number | null,
    versionNumber?: number | null,
  ): void
  stopEditing(): void
  /** Records a save's answer. `workflowId` is the session the save STARTED in:
   * an answer that lands after Exit or after navigating to another workflow
   * (SA-1410) changes nothing — it must not plant its stamp on the next session. */
  markSaved(draft: WorkflowOverlayJson, updatedAt: string | null | undefined, workflowId: string): void
  /** SA-1410: the session moves onto what is stored and live now — `start`
   * becomes the overlay its edits are measured against, `overlay` (the
   * caller's rebase of the edits over `start`) the working copy, with the
   * new base, stamp and numbers. Validation is for the old basis; drop it. */
  rebaseOnto(
    base: OrderWorkflowDefinitionJson,
    start: WorkflowOverlayJson,
    overlay: WorkflowOverlayJson,
    expectedUpdatedAt: string | null,
    baseVersionNumber: number | null,
    versionNumber: number | null,
  ): void

  setAskText(key: string, lang: string, text: string): void
  setTemplateText(key: string, lang: string, text: string): void
  setParkMessage(lang: string, text: string): void
  setAlwaysRequired(slotKey: string, value: boolean): void
  setAskPriority(slotKey: string, value: number): void
  setOptions(slotKey: string, options: string[]): void
  setPromptHint(slotKey: string, value: string): void
  setCooldown(minutes: number | null): void
  /** SA-110 parameter write-through: one generic pruning setter — a manifest
   * parameter's `path` is the overlay address itself (dotted). Locked
   * parameters never render an editor, so no locked guard here; the server's
   * overlay_parameter_locked finding is the backstop. */
  setParameter(path: string, value: unknown, baseValue: unknown): void
  setValidation(v: WorkflowConfigValidateResponse | null): void
  setAdvanced(v: boolean): void

  addSlot(key: string, spec: Record<string, unknown>, asks: Record<string, string>): void
  addAutomation(section: 'derived' | 'side_effects', key: string, spec: Record<string, unknown>): void
  disableEntry(section: KeyedSection, key: string): void
  enableEntry(section: KeyedSection, key: string): void

  addCheckpointRequirement(cpKey: string, slotKey: string): void
  removeCheckpointRequirement(cpKey: string, slotKey: string): void

  setEscalationMessage(key: string, lang: string, text: string): void
  setTriggerField(
    id: string,
    field: 'enabled' | 'mode' | 'after_turns' | 'reset_after_minutes' | 'after' | 'above',
    value: unknown,
  ): void
  setTriggerKeywords(id: string, lang: string, keywords: string[]): void
  addKeywordTrigger(id: string, spec: Record<string, unknown>): void
  removeAddedTrigger(id: string): void

  setSignalInstruction(id: string, lang: string, text: string): void
  setSignalEnabled(id: string, value: boolean): void
  addSignal(id: string, spec: Record<string, unknown>): void
  removeAddedSignal(id: string): void

  isDirty(): boolean
  overlaySnapshot(): WorkflowOverlayJson
}

export const useWorkflowConfigStore = create<WorkflowConfigState>((set, get) => ({
  workflowId: null,
  base: null,
  overlay: {},
  savedDraft: null,
  expectedUpdatedAt: null,
  baseVersionNumber: null,
  versionNumber: null,
  editing: false,
  validation: null,
  advanced: false,

  beginEditing: (workflowId, base, savedDraft, expectedUpdatedAt = null, baseVersionNumber = null, versionNumber = null) =>
    set({
      workflowId,
      base,
      overlay: savedDraft ? clone(savedDraft) : {},
      savedDraft: savedDraft ? clone(savedDraft) : null,
      expectedUpdatedAt,
      baseVersionNumber,
      versionNumber,
      editing: true,
      validation: null,
      advanced: false,
    }),
  stopEditing: () =>
    set({
      workflowId: null,
      base: null,
      overlay: {},
      savedDraft: null,
      expectedUpdatedAt: null,
      baseVersionNumber: null,
      versionNumber: null,
      editing: false,
      validation: null,
      advanced: false,
    }),
  markSaved: (draft, updatedAt, workflowId) => {
    if (get().workflowId !== workflowId) return
    set(updatedAt === undefined ? { savedDraft: clone(draft) } : { savedDraft: clone(draft), expectedUpdatedAt: updatedAt })
  },
  rebaseOnto: (base, start, overlay, expectedUpdatedAt, baseVersionNumber, versionNumber) =>
    set({
      base,
      savedDraft: clone(start),
      overlay: clone(overlay),
      expectedUpdatedAt,
      baseVersionNumber,
      versionNumber,
      validation: null,
    }),

  setAskText: (key, lang, text) => {
    const { base, overlay } = get()
    const baseValue = base?.conversation?.asks?.[key]?.[lang]
    set({ overlay: setOrPrune(overlay, ['conversation', 'asks', key, lang], text, baseValue) })
  },
  setTemplateText: (key, lang, text) => {
    const { base, overlay } = get()
    const baseValue = base?.templates?.[key]?.[lang]
    set({ overlay: setOrPrune(overlay, ['templates', key, lang], text, baseValue) })
  },
  setParkMessage: (lang, text) => {
    const { base, overlay } = get()
    const baseValue = base?.conversation?.park_message?.[lang]
    set({ overlay: setOrPrune(overlay, ['conversation', 'park_message', lang], text, baseValue) })
  },
  setAlwaysRequired: (slotKey, value) => {
    const { base, overlay } = get()
    const baseValue = base?.slots?.[slotKey]?.always_required ?? false
    set({ overlay: setOrPrune(overlay, ['slots', slotKey, 'always_required'], value, baseValue) })
  },
  setAskPriority: (slotKey, value) => {
    const { base, overlay } = get()
    // Needs (e.g. `cart_non_empty`) also render the "Ask order" input, but
    // they live in a separate keyed overlay section — writing them under
    // `slots` fabricates a bogus slot entry that fails validation. Overlay-
    // added needs (no base anchor, SA-83 orphan shape) route the same way,
    // unless the key collides with a base SLOT (isNeedKey excludes that).
    const needsBase = (base?.needs as Record<string, { ask_priority?: number }> | undefined)?.[
      slotKey
    ]
    if (isNeedKey(base, overlay, slotKey)) {
      const baseValue = needsBase?.ask_priority ?? 100
      const next = setOrPrune(overlay, ['needs', slotKey, 'ask_priority'], value, baseValue) as Record<
        string,
        unknown
      >
      if (!needsBase && !needEntryPresent(next, slotKey)) {
        // Overlay-added (orphan) need fully collapsed (last field cleared) —
        // spec: this "collapses the entry out of the overlay ENTIRELY", so
        // any stranded conversation.asks override for it must go too, or
        // the agenda row vanishes while a publish-blocking ask survives.
        deletePath(next, ['conversation', 'asks', slotKey])
      }
      set({ overlay: next as WorkflowOverlayJson })
      return
    }
    const baseValue = base?.slots?.[slotKey]?.ask_priority ?? 100
    set({ overlay: setOrPrune(overlay, ['slots', slotKey, 'ask_priority'], value, baseValue) })
  },
  setOptions: (slotKey, options) => {
    const { base, overlay } = get()
    const baseValue = base?.slots?.[slotKey]?.options ?? null
    set({ overlay: setOrPrune(overlay, ['slots', slotKey, 'options'], options, baseValue) })
  },
  setPromptHint: (slotKey, value) => {
    const { base, overlay } = get()
    // Needs carry prompt_hint too but live in their own keyed section —
    // route like setAskPriority (including overlay-added needs, SA-83).
    // Base '' when absent so clearing prunes to base.
    const needsBase = (base?.needs as Record<string, { prompt_hint?: string }> | undefined)?.[
      slotKey
    ]
    const isNeed = isNeedKey(base, overlay, slotKey)
    const section = isNeed ? 'needs' : 'slots'
    const baseValue =
      (needsBase ? needsBase.prompt_hint : isNeed ? undefined : base?.slots?.[slotKey]?.prompt_hint) ?? ''
    const next = setOrPrune(overlay, [section, slotKey, 'prompt_hint'], value, baseValue) as Record<
      string,
      unknown
    >
    if (isNeed && !needsBase && !needEntryPresent(next, slotKey)) {
      // Overlay-added (orphan) need fully collapsed — same stranded-asks
      // cleanup as setAskPriority above.
      deletePath(next, ['conversation', 'asks', slotKey])
    }
    set({ overlay: next as WorkflowOverlayJson })
  },
  setCooldown: (minutes) => {
    const { base, overlay } = get()
    const baseValue = base?.post_completion_cooldown_minutes ?? null
    set({
      overlay: setOrPrune(
        overlay,
        ['settings', 'post_completion_cooldown_minutes'],
        minutes,
        baseValue,
      ),
    })
  },
  setParameter: (path, value, baseValue) =>
    set((s) => ({
      overlay: setOrPrune(s.overlay, path.split('.'), value, baseValue),
    })),

  setValidation: (v) => set({ validation: v }),
  setAdvanced: (v) => set({ advanced: v }),

  addSlot: (key, spec, asks) => {
    // Spec objects must be undefined-free: clone() is a JSON round-trip
    // and would silently drop them — callers OMIT absent fields instead.
    const next = clone(get().overlay) as Record<string, unknown>
    next.slots = { ...(next.slots as object | undefined), [key]: clone(spec) }
    const conv = { ...(next.conversation as Record<string, unknown> | undefined) }
    conv.asks = { ...(conv.asks as object | undefined), [key]: { ...asks } }
    next.conversation = conv
    set({ overlay: next as WorkflowOverlayJson })
  },
  addAutomation: (section, key, spec) => {
    const next = clone(get().overlay) as Record<string, unknown>
    next[section] = { ...(next[section] as object | undefined), [key]: clone(spec) }
    set({ overlay: next as WorkflowOverlayJson })
  },
  disableEntry: (section, key) => {
    const { base, overlay } = get()
    const baseSection = (base as unknown as Record<string, Record<string, unknown> | undefined>)?.[
      section
    ]
    const next = clone(overlay) as Record<string, unknown>
    if (baseSection && key in baseSection) {
      // Base entry -> exact tombstone (validated shape: nothing else in it).
      next[section] = { ...(next[section] as object | undefined), [key]: { disabled: true } }
    } else {
      // Overlay-added entry -> just un-add it.
      deletePath(next, [section, key])
    }
    if (section === 'slots' || section === 'needs') {
      // The merge applies conversation patches AFTER tombstones — a
      // surviving ask override would resurrect an ask for an undeclared
      // slot and fail validation. Prune it either way.
      deletePath(next, ['conversation', 'asks', key])
    }
    set({ overlay: next as WorkflowOverlayJson })
  },
  enableEntry: (section, key) => {
    const next = clone(get().overlay) as Record<string, unknown>
    deletePath(next, [section, key])
    set({ overlay: next as WorkflowOverlayJson })
  },

  addCheckpointRequirement: (cpKey, slotKey) => {
    const { base, overlay } = get()
    const baseRequires = base?.checkpoints?.[cpKey]?.requires ?? []
    const current =
      (overlay.checkpoints?.[cpKey] as { requires?: string[] } | undefined)?.requires ??
      baseRequires
    if (current.includes(slotKey)) return
    const next = [...current, slotKey]
    set({
      overlay: setOrPrune(
        overlay,
        ['checkpoints', cpKey, 'requires'],
        sameMembership(next, baseRequires) ? baseRequires : next,
        baseRequires,
      ),
    })
  },
  removeCheckpointRequirement: (cpKey, slotKey) => {
    const { base, overlay } = get()
    const baseRequires = base?.checkpoints?.[cpKey]?.requires ?? []
    const current =
      (overlay.checkpoints?.[cpKey] as { requires?: string[] } | undefined)?.requires ??
      baseRequires
    const next = current.filter((k) => k !== slotKey)
    set({
      overlay: setOrPrune(
        overlay,
        ['checkpoints', cpKey, 'requires'],
        sameMembership(next, baseRequires) ? baseRequires : next,
        baseRequires,
      ),
    })
  },

  setEscalationMessage: (key, lang, text) => {
    const { base, overlay } = get()
    const baseValue = base?.escalation?.messages?.[key]?.[lang]
    set({
      overlay: setOrPrune(overlay, ['escalation', 'messages', key, lang], text, baseValue),
    })
  },
  setTriggerField: (id, field, value) => {
    const { base, overlay } = get()
    const trig = findBaseTrigger(base, id)
    // ADDED triggers have no base entry: baseValue stays undefined, so a
    // real value never prunes and the added spec stays complete.
    const baseValue = trig ? (trig[field] ?? TRIGGER_FIELD_DEFAULTS[field]) : undefined
    set({
      overlay: setOrPrune(overlay, ['escalation', 'triggers', id, field], value, baseValue),
    })
  },
  setTriggerKeywords: (id, lang, keywords) => {
    const { base, overlay } = get()
    const trig = findBaseTrigger(base, id)
    const baseValue = (trig?.match as Record<string, string[]> | undefined)?.[lang]
    set({
      overlay: setOrPrune(
        overlay,
        ['escalation', 'triggers', id, 'match', lang],
        keywords,
        baseValue,
      ),
    })
  },
  addKeywordTrigger: (id, spec) => {
    // Spec objects must be undefined-free (clone() is a JSON round-trip) —
    // callers OMIT absent fields, mirroring addSlot.
    const next = clone(get().overlay) as Record<string, unknown>
    const esc = { ...(next.escalation as Record<string, unknown> | undefined) }
    esc.triggers = { ...(esc.triggers as object | undefined), [id]: clone(spec) }
    next.escalation = esc
    set({ overlay: next as WorkflowOverlayJson })
  },
  removeAddedTrigger: (id) => {
    const next = clone(get().overlay) as Record<string, unknown>
    deletePath(next, ['escalation', 'triggers', id])
    set({ overlay: next as WorkflowOverlayJson })
  },

  setSignalInstruction: (id, lang, text) => {
    const { base, overlay } = get()
    // ADDED signals have no base entry: baseValue stays undefined, so a real
    // value never prunes and the added spec stays complete.
    const baseValue = (findBaseSignal(base, id)?.instruction as
      | Record<string, string>
      | undefined)?.[lang]
    set({
      overlay: setOrPrune(overlay, ['signals', id, 'instruction', lang], text, baseValue),
    })
  },
  setSignalEnabled: (id, value) => {
    const { base, overlay } = get()
    const sig = findBaseSignal(base, id)
    const baseValue = sig ? (sig.enabled ?? SIGNAL_FIELD_DEFAULTS.enabled) : undefined
    set({
      overlay: setOrPrune(overlay, ['signals', id, 'enabled'], value, baseValue),
    })
  },
  addSignal: (id, spec) => {
    // Spec objects must be undefined-free (clone() is a JSON round-trip) —
    // callers OMIT absent fields, mirroring addKeywordTrigger.
    const next = clone(get().overlay) as Record<string, unknown>
    next.signals = { ...(next.signals as object | undefined), [id]: clone(spec) }
    set({ overlay: next as WorkflowOverlayJson })
  },
  removeAddedSignal: (id) => {
    const next = clone(get().overlay) as Record<string, unknown>
    deletePath(next, ['signals', id])
    set({ overlay: next as WorkflowOverlayJson })
  },

  isDirty: () => {
    const { overlay, savedDraft } = get()
    return !deepEqual(overlay, savedDraft ?? {})
  },
  overlaySnapshot: () => clone(get().overlay),
}))
